import {
    Chip, Grid, Skeleton, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Tooltip, IconButton,
} from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import { iconSize } from 'assets/theme/iconSize';
import { setLoading, useMaterialUIController } from 'context';
import { getCrmColors } from '@layouts/pages/CRM/kanban-board/crm-theme';
import { useAlert } from '@components/showAlert/useAlertHook';
import { useTranslation } from 'react-i18next';
import { apiErrorMessage } from '@functions/api-error-message';
import { LossReason, LossReasonService } from '../services/loss-reason.service';

interface Props {
    /// Nulo enquanto carrega.
    reasons: LossReason[] | null;
    onEdit: (reason: LossReason) => void;
    onChanged: () => void;
}

export function LossReasonTable({ reasons, onEdit, onChanged }: Props) {
    const { t } = useTranslation();
    const [controller, dispatch] = useMaterialUIController();
    const cc = getCrmColors(controller.darkMode);
    const { showAlert, alertDelete } = useAlert();

    const doDelete = (id: string) => {
        setLoading(dispatch, true);

        new LossReasonService()
            .remove(id)
            .then(() => {
                showAlert(t('motivosPerda.removido'), 'success', 2500);
                onChanged();
            })
            // Motivo já usado não sai: o backend recusa e explica o porquê.
            .catch((err) => showAlert(apiErrorMessage(err), 'error', 5000))
            .finally(() => setLoading(dispatch, false));
    };

    return (
        <Grid>
            <TableContainer sx={{ marginTop: '15px', maxHeight: '60vh', overflowY: 'auto' }}>
                <Table size="medium">
                    <TableHead sx={{ position: 'sticky', top: 0, backgroundColor: 'background.card' }}>
                        <TableRow hover>
                            <TableCell width="60%">{t('general.nome')}</TableCell>
                            <TableCell width="25%">{t('motivosPerda.situacao')}</TableCell>
                            <TableCell width="15%" align="center">{t('general.acoes')}</TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {reasons === null ? (
                            Array.from({ length: 5 }, (_, index) => (
                                <TableRow hover key={index}>
                                    {[0, 1, 2].map((cell) => (
                                        <TableCell key={cell}>
                                            <Skeleton height={30} variant="rectangular" />
                                        </TableCell>
                                    ))}
                                </TableRow>
                            ))
                        ) : reasons.length === 0 ? (
                            <TableRow hover>
                                <TableCell colSpan={3} align="center">
                                    {t('general.nenhumRegistro')}
                                </TableCell>
                            </TableRow>
                        ) : (
                            reasons.map((reason) => (
                                <TableRow hover key={reason.id}>
                                    <TableCell>{reason.name}</TableCell>
                                    <TableCell>
                                        <Chip
                                            size="small"
                                            variant="outlined"
                                            color={reason.active ? 'success' : 'default'}
                                            label={reason.active ? t('general.ativo') : t('general.inativo')}
                                            sx={{ '& .MuiChip-label': { color: cc.onTint } }}
                                        />
                                    </TableCell>
                                    <TableCell>
                                        <div style={{ display: 'flex', justifyContent: 'center' }}>
                                            <Tooltip title={t('general.editar')}>
                                                <IconButton size="small" sx={{ p: 0.25 }} onClick={() => onEdit(reason)}>
                                                    <EditIcon style={iconSize} />
                                                </IconButton>
                                            </Tooltip>
                                            <Tooltip title={t('general.excluir')}>
                                                <IconButton size="small" sx={{ p: 0.25 }} onClick={() => alertDelete(reason.id, doDelete, reason.name)}>
                                                    <DeleteIcon
                                                        sx={iconSize}
                                                        color="error"
                                                    />
                                                </IconButton>
                                            </Tooltip>
                                        </div>
                                    </TableCell>
                                </TableRow>
                            ))
                        )}
                    </TableBody>
                </Table>
            </TableContainer>
        </Grid>
    );
}
